import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MasterDataCategoriesApi } from './api';
import { masterDataQueryKeys } from '@/lib/sdk/master-data/queries';
import type { CreateMasterDataCategoryDto, UpdateMasterDataCategoryDto } from './types';

const QUERY_KEYS = {
  all: ['master-data-categories'] as const,
  list: (orgId: string) => [...QUERY_KEYS.all, orgId] as const,
};

export const useMasterDataCategories = (orgId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.list(orgId),
    queryFn: () => MasterDataCategoriesApi.list(orgId),
    enabled: !!orgId,
  });
};

export const useCreateMasterDataCategory = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateMasterDataCategoryDto) => MasterDataCategoriesApi.create(orgId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: masterDataQueryKeys.datasets(orgId) });
    },
  });
};

export const useUpdateMasterDataCategory = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, dto }: { categoryId: number; dto: UpdateMasterDataCategoryDto }) =>
      MasterDataCategoriesApi.update(orgId, categoryId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: masterDataQueryKeys.datasets(orgId) });
    },
  });
};

export const useDeleteMasterDataCategory = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: number) => MasterDataCategoriesApi.remove(orgId, categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: masterDataQueryKeys.datasets(orgId) });
    },
  });
};

export { QUERY_KEYS as categoryQueryKeys };
